import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Select, Card, Space, message } from 'antd';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { WORKFLOW_CONFIG } from 'src/enums/EOutbound';
import { useOutbound } from 'src/Hooks/SmartCRM/useOutbound';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgentId } from '@/store/account/selector';

const { TextArea } = Input;
const { Option } = Select;

interface CustomWorkflowFormProps {
	type: string;
	onCancel: () => void;
	onSave: (data: any) => void;
	editingWorkflow?: any;
}

export const CustomWorkflowForm: React.FC<CustomWorkflowFormProps> = ({
	type,
	onCancel,
	onSave,
	editingWorkflow,
}) => {
	const [form] = Form.useForm();
	const [questions, setQuestions] = useState<string[]>(['']);
	const { createWorkflowMutation, updateWorkflowMutation } = useOutbound();
	const selectedAgentId = useAppSelector(getSelectedAgentId);

	const config = WORKFLOW_CONFIG[type as keyof typeof WORKFLOW_CONFIG] || {
		title: type.charAt(0).toUpperCase() + type.slice(1),
		description: `${type} workflow`,
		color: 'blue',
	};
	const isEditing = !!editingWorkflow;

	useEffect(() => {
		if (editingWorkflow) {
			form.setFieldsValue({
				customScenario: editingWorkflow.scenario,
				description: editingWorkflow.scenario,
				textMessage: editingWorkflow.data?.textMessage,
				phoneNumber: editingWorkflow.data?.phoneNumber,
				extension: editingWorkflow.data?.extension,
				voiceResponse: editingWorkflow.data?.voiceResponse,
				postExecutionPrompt: editingWorkflow.data?.postExecutionPrompt,
				shortURLEnabled: editingWorkflow.data?.shortURLEnabled || false,
				askForPermissionOnText: editingWorkflow.data?.askForPermissionOnText || false,
			});

			if (editingWorkflow.data?.questions && Array.isArray(editingWorkflow.data.questions)) {
				setQuestions(
					editingWorkflow.data.questions.length > 0 ? editingWorkflow.data.questions : [''],
				);
			}
		} else {
			form.resetFields();
			setQuestions(['']);
		}
	}, [editingWorkflow, form]);

	const addQuestion = () => {
		setQuestions([...questions, '']);
	};

	const removeQuestion = (index: number) => {
		setQuestions(questions.filter((_, i) => i !== index));
	};

	const updateQuestion = (index: number, value: string) => {
		const newQuestions = [...questions];
		newQuestions[index] = value;
		setQuestions(newQuestions);
	};

	const handleSubmit = async (values: any) => {
		if (!selectedAgentId) {
			message.error('No agent selected');
			return;
		}

		const workflowData = {
			type: type.toUpperCase(),
			name: editingWorkflow?.name || `Custom ${config.title}`,
			scenario: values.description || values.customScenario,
			enabled: true,
			data: {
				...values,
				...(type.toUpperCase() === 'INTAKE' && {
					questions: questions.filter((q) => q.trim()),
				}),
			},
		};

		try {
			if (isEditing) {
				await updateWorkflowMutation.mutateAsync({
					clientId: selectedAgentId,
					workflowId: editingWorkflow.id,
					workflowData,
				});
				message.success('Workflow updated successfully');
			} else {
				await createWorkflowMutation.mutateAsync({
					clientId: selectedAgentId,
					workflowData,
				});
				message.success('Workflow created successfully');
			}
			onSave(workflowData);
		} catch (_error) {
			message.error(`Failed to ${isEditing ? 'update' : 'create'} workflow`);
		}
	};

	const renderIntakeFields = () => (
		<>
			<Form.Item
				name="customScenario"
				label="Scenario Description"
				rules={[{ required: true, message: 'Please enter scenario description' }]}
			>
				<TextArea
					rows={3}
					placeholder="When a caller wants to schedule an appointment, collect their availability."
				/>
			</Form.Item>

			<Form.Item label="Questions">
				<Space direction="vertical" className="w-full">
					{questions.map((question, index) => (
						<div key={index} className="flex gap-2">
							<Input
								value={question}
								onChange={(e) => updateQuestion(index, e.target.value)}
								placeholder={'What days of the week work best for you?'}
								className="flex-1"
							/>
							{questions.length > 1 && (
								<Button
									type="text"
									icon={<CloseOutlined />}
									onClick={() => removeQuestion(index)}
									className="flex-shrink-0"
								/>
							)}
						</div>
					))}
					<Button type="dashed" onClick={addQuestion} icon={<PlusOutlined />} className="w-full">
						Add Question
					</Button>
				</Space>
			</Form.Item>

			<Form.Item
				name="postExecutionPrompt"
				label="What should the AI do next?"
				rules={[{ required: true, message: 'Please enter what AI should do next' }]}
			>
				<TextArea
					rows={2}
					placeholder="Thank the caller for providing their availability information..."
				/>
			</Form.Item>
		</>
	);

	const renderSMSFields = () => (
		<>
			<Form.Item
				name="description"
				label="Scenario Description"
				rules={[{ required: true, message: 'Please enter scenario description' }]}
			>
				<Input placeholder="When a user asks for the pricing list." />
			</Form.Item>

			<Form.Item
				name="textMessage"
				label="Text Message"
				rules={[{ required: true, message: 'Please enter text message' }]}
			>
				<TextArea rows={3} placeholder="Here is the list" />
			</Form.Item>

			<Form.Item name="shortURLEnabled" label="Enable Short URLs">
				<Select defaultValue={false}>
					<Option value={true}>Yes</Option>
					<Option value={false}>No</Option>
				</Select>
			</Form.Item>

			<Form.Item
				name="postExecutionPrompt"
				label="What should the AI do next?"
				rules={[{ required: true, message: 'Please enter what AI should do next' }]}
			>
				<TextArea rows={2} placeholder="Thank you" />
			</Form.Item>

			<Form.Item name="askForPermissionOnText" label="Ask before sending Text SMS">
				<Select defaultValue={false}>
					<Option value={true}>Yes</Option>
					<Option value={false}>No</Option>
				</Select>
			</Form.Item>
		</>
	);

	const renderTransferFields = () => (
		<>
			<Form.Item
				name="description"
				label="Scenario Description"
				rules={[{ required: true, message: 'Please enter scenario description' }]}
			>
				<Input placeholder="When a customer asks to speak with a real person" />
			</Form.Item>

			<Form.Item
				name="phoneNumber"
				label="Phone Number"
				rules={[{ required: true, message: 'Please enter phone number' }]}
			>
				<Input placeholder="+1234567890" />
			</Form.Item>

			<Form.Item name="extension" label="Extension (Optional)">
				<Input placeholder="123" />
			</Form.Item>

			<Form.Item
				name="voiceResponse"
				label="Voice Response"
				rules={[{ required: true, message: 'Please enter voice response' }]}
			>
				<TextArea rows={2} placeholder="I'm transferring you to our team now..." />
			</Form.Item>
		</>
	);

	const renderFields = () => {
		const upperType = type.toUpperCase();
		switch (upperType) {
			case 'INTAKE':
				return renderIntakeFields();
			case 'SMS':
				return renderSMSFields();
			case 'TRANSFER':
				return renderTransferFields();
			default:
				return renderIntakeFields();
		}
	};

	return (
		<Card title={`${isEditing ? 'Edit' : 'Create'} ${config.title}`} className="mt-6">
			<Form form={form} layout="vertical" onFinish={handleSubmit}>
				{renderFields()}

				<div className="flex justify-end gap-2 mt-6">
					<Button onClick={onCancel}>Cancel</Button>
					<Button
						type="primary"
						htmlType="submit"
						loading={createWorkflowMutation.isPending || updateWorkflowMutation.isPending}
					>
						{isEditing ? 'Update' : 'Save'} Workflow
					</Button>
				</div>
			</Form>
		</Card>
	);
};
